import { useServiceTemplateOptions } from '@hooks';
import { L } from '@lib';
import { ServiceTemplate } from '@services';
import { Select } from 'antd';
import { observer } from 'mobx-react';
import React, { useCallback } from 'react';

interface IProps {
  value?: number;
  /** Hands back the whole template, not just its id - callers expand `services` straight away. */
  onChange?: (templateId?: number, template?: ServiceTemplate) => void;
  placeholder?: string;
  allowClear?: boolean;
  disabled?: boolean;
  size?: 'small' | 'middle' | 'large';
  style?: React.CSSProperties;
}

/**
 * Picks one template to build a master build from.
 *
 * Distinct from ServiceTemplateSelect, which is the multi-select used the other way round (assigning
 * one service to several templates). Sharing a component would mean a mode flag on every prop.
 */
export const ServiceTemplatePicker: React.FC<IProps> = observer(
  ({ value, onChange, placeholder, allowClear = true, disabled = false, size, style }) => {
    const { templates, loading } = useServiceTemplateOptions();

    // Only templates with services on them can populate a build.
    const options = templates
      .filter((t) => t.isActive && (t.serviceCount ?? 0) > 0)
      .map((t) => ({
        label: `${t.name} (${t.serviceCount} ${t.serviceCount === 1 ? L('job') : L('jobs')})`,
        value: t.id,
      }));

    const handleChange = useCallback(
      (next?: number) => onChange?.(next, templates.find((t) => t.id === next)),
      [onChange, templates]
    );

    return (
      <Select
        showSearch
        optionFilterProp="label"
        loading={loading}
        disabled={disabled}
        allowClear={allowClear}
        notFoundContent={loading ? null : L('No templates with services yet')}
        style={{ width: '100%', ...style }}
        size={size}
        value={value}
        options={options}
        onChange={handleChange}
        placeholder={placeholder || L('Select a template')}
      />
    );
  }
);
